import {
  Callout,
  Checkbox,
  Classes,
  Intent,
  PanelProps,
} from '@blueprintjs/core';
import classNames from 'classnames';
import uniq from 'lodash/uniq';
import * as React from 'react';

import { EditableLabel } from 'src/components/EditableLabel';
import {
  TEST_IDS,
  getPublishProjectDrawerPrefixRowTestId,
  getPublishProjectDrawerShouldPublishCheckboxTestId,
} from 'src/constants/testIds';
import { usePublish } from 'src/containers/PublishProjectDrawer/hooks';
import {
  MappingConfigPanel,
  MappingConfigPanelProps,
} from 'src/containers/PublishProjectDrawer/MappingConfigPanel';
import {
  ModelAliasConfigPanel,
  ModelAliasConfigPanelProps,
} from 'src/containers/PublishProjectDrawer/ModelAliasConfigPanel';
import {
  OpenInExplorerPanel,
  OpenInExplorerPanelProps,
} from 'src/containers/PublishProjectDrawer/OpenInExplorerPanel';
import { PublishProjectDrawerFooter } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerFooter';
import { PublishProjectDrawerStepper } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerStepper';
import {
  DatabaseInfo,
  ModelsInfo,
  PrefixInfo,
  RulesInfo,
  SensitivePropertyGroupsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { hasValidMappingsSelector } from 'src/selectors/hasValidMappings';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import {
  INVALID_IRI_MATCHER,
  IRI_STARTS_WITH_MATCHER,
} from 'src/utils/removeInvalidIriChars';

export type ModelNamespacePanelProps = {
  databaseInfo: DatabaseInfo;
  initialPrefixInfoArr: PrefixInfo[];
  isConsolidatingModels: boolean;
  isPublishingRules: boolean;
  modelsInfo: ModelsInfo;
  ruleInfo: RulesInfo;
  spgInfo: SensitivePropertyGroupsInfo;
};

type ModelNamespaceError = {
  namespace: string;
  prefix: string;
  prefixErrorIntent: Intent;
};

type ModelNamespaceRowProps = {
  error: ModelNamespaceError;
  index: number;
  onUpdatePrefixInfo: (index: number, prefixInfo: Partial<PrefixInfo>) => void;
  prefixInfo: PrefixInfo;
};

enum ModelNamespacePanelNextButtonType {
  COMBO_MODEL = 'COMBO_MODEL',
  MAPPING = 'MAPPING',
  PUBLISH = 'PUBLISH',
}

const validateIri = (iri: string): string => {
  if (!iri) {
    return copy.components.publishProjectDrawer.namespace.validation
      .namespaceRequired;
  }

  if (!iri.match(IRI_STARTS_WITH_MATCHER)) {
    return copy.components.publishProjectDrawer.namespace.validation
      .namespaceStartsWith;
  }

  if (!iri.includes(':')) {
    return copy.components.publishProjectDrawer.namespace.validation
      .namespaceSemicolon;
  }

  const invalidChars = iri.match(INVALID_IRI_MATCHER);
  if (invalidChars) {
    return copy.components.publishProjectDrawer.namespace.validation.namespaceChars(
      uniq(invalidChars)
    );
  }

  return '';
};

const ModelNamespaceRow: React.VFC<ModelNamespaceRowProps> = ({
  error,
  index,
  onUpdatePrefixInfo,
  prefixInfo,
}) => {
  return (
    <div
      className="sd-prefix-row"
      // nothing about the prefix is uniquely identifiable if the user makes two rows
      // that have the same prefix/namespace combo
      data-testid={getPublishProjectDrawerPrefixRowTestId(
        index.toString(),
        index
      )}
    >
      <div className="sd-prefix-row__body">
        <div className="sd-prefix-grid">
          <div className="sd-prefix-grid__should-publish">
            <Checkbox
              checked={prefixInfo.shouldPublish}
              data-testid={getPublishProjectDrawerShouldPublishCheckboxTestId(
                `${prefixInfo.prefix}-${prefixInfo.namespace}`
              )}
              disabled={Boolean(error.prefix)}
              label={undefined}
              onChange={({ currentTarget: { checked } }) => {
                onUpdatePrefixInfo(index, { shouldPublish: checked });
              }}
            />
          </div>
          <Callout
            className="sd-prefix-grid__prefix"
            icon={null}
            intent={error.prefixErrorIntent}
          >
            <EditableLabel
              errorIntent={error.prefixErrorIntent}
              onSetValue={(prefix) => {
                onUpdatePrefixInfo(index, { prefix });
              }}
              onValidate={() => error.prefix}
              placeholder={
                copy.components.projectSettingsDrawer.prefixTable
                  .prefixPlaceholder
              }
              setValueOnChange
              value={prefixInfo.prefix}
            />
          </Callout>
          <Callout
            className="sd-prefix-grid__namespace"
            icon={null}
            intent={error.namespace ? Intent.DANGER : Intent.NONE}
          >
            <EditableLabel
              errorIntent={Intent.DANGER}
              onSetValue={(namespace) =>
                onUpdatePrefixInfo(index, { namespace })
              }
              onValidate={() => error.namespace}
              placeholder={
                copy.components.projectSettingsDrawer.prefixTable
                  .namespacePlaceholder
              }
              setValueOnChange
              value={prefixInfo.namespace}
            />
          </Callout>
        </div>
      </div>
    </div>
  );
};

export const ModelNamespacePanel: React.VFC<
  PanelProps<ModelNamespacePanelProps>
> = ({
  closePanel,
  initialPrefixInfoArr,
  isConsolidatingModels,
  isPublishingRules,
  databaseInfo,
  modelsInfo,
  ruleInfo,
  spgInfo,
  openPanel,
}) => {
  const { databaseId } = databaseInfo;
  const { projectId } = useRouterParams();
  const publish = usePublish();
  const project = useReduxSelector(projectSelector(projectId));
  const databasesById = useReduxSelector(
    (state) => state.databases.databasesById
  );
  const databaseNamespaces = useReduxSelector(
    (state) => state.databases.databasesById[databaseId]?.namespaces || {}
  );
  const hasValidMappings = useReduxSelector(
    hasValidMappingsSelector(projectId)
  );

  const [prefixInfoArr, setPrefixInfoArr] =
    React.useState(initialPrefixInfoArr);

  const didProjectChange = useDidValueChange(project);
  React.useEffect(() => {
    if (didProjectChange) {
      setPrefixInfoArr(initialPrefixInfoArr);
    }
  }, [didProjectChange, initialPrefixInfoArr]);

  const handleValidatePrefix = React.useCallback(
    (
      // we have to validate using future values when prefixes are being set,
      // so we pass in this array instead of using the existing array
      existingPrefixInfoArr: PrefixInfo[],
      prefix: string
    ): { message: string; intent: Intent } => {
      if (!prefix) {
        return {
          message:
            copy.components.publishProjectDrawer.namespace.validation
              .prefixRequired,
          intent: Intent.DANGER,
        };
      }

      // warning intent is used for prefixes existing on db
      // error intent is used for duplicates on current form
      if (databaseNamespaces[prefix]) {
        return {
          message:
            copy.components.publishProjectDrawer.namespace.validation
              .prefixExistsOnDatabase,
          intent: Intent.WARNING,
        };
      }

      let foundNum = 0;
      const areThereDupes = existingPrefixInfoArr.some((prefixInfo) => {
        if (prefixInfo.prefix === prefix) {
          foundNum += 1;
        }

        return foundNum === 2;
      });

      return areThereDupes
        ? {
            message:
              copy.components.publishProjectDrawer.namespace.validation
                .prefixMustBeUnique,
            intent: Intent.DANGER,
          }
        : { message: '', intent: Intent.NONE };
    },
    [databaseNamespaces]
  );

  const prefixErrors = React.useMemo<
    Array<{
      namespace: string;
      prefix: string;
      prefixErrorIntent: Intent;
    }>
  >(() => {
    return prefixInfoArr.map((prefixInfo) => {
      if (!prefixInfo.isConflicted) {
        return {
          namespace: '',
          prefix: '',
          prefixErrorIntent: Intent.NONE,
        };
      }

      const { message, intent } = handleValidatePrefix(
        prefixInfoArr,
        prefixInfo.prefix
      );
      return {
        namespace: validateIri(prefixInfo.namespace),
        prefix: message,
        prefixErrorIntent: intent,
      };
    });
  }, [handleValidatePrefix, prefixInfoArr]);
  const nextButtonError = React.useMemo(() => {
    let nextError = '';

    prefixErrors.some(({ prefix, namespace, prefixErrorIntent }) => {
      if (namespace) {
        nextError = namespace;
        return true;
      }

      if (prefixErrorIntent === Intent.DANGER) {
        nextError = prefix;
        return true;
      }

      return false;
    });

    return nextError;
  }, [prefixErrors]);

  const handleUpdatePrefixInfo = (
    idx: number,
    partialPrefixInfo: Partial<PrefixInfo>
  ) => {
    if (!prefixInfoArr[idx]) {
      return;
    }

    const updatedPrefixInfo: PrefixInfo = {
      ...prefixInfoArr[idx],
      ...partialPrefixInfo,
    };
    let nextPrefixInfoArr = [...prefixInfoArr];
    nextPrefixInfoArr.splice(idx, 1, updatedPrefixInfo);
    const { intent } = handleValidatePrefix(
      nextPrefixInfoArr,
      partialPrefixInfo.prefix || prefixInfoArr[idx].prefix
    );

    // disable checkbox(es) if prefix is invalid due to existing on database, or being a dupe
    if (intent === Intent.WARNING) {
      updatedPrefixInfo.shouldPublish = false;
    } else if (intent === Intent.DANGER) {
      nextPrefixInfoArr = nextPrefixInfoArr.map((prefixInfo) => ({
        ...prefixInfo,
        shouldPublish:
          prefixInfo.prefix === updatedPrefixInfo.prefix
            ? false
            : prefixInfo.shouldPublish,
      }));
    }

    setPrefixInfoArr(nextPrefixInfoArr);
  };

  const [nextButtonType, nextButtonTestId] = React.useMemo<
    [ModelNamespacePanelNextButtonType, string]
  >(() => {
    const publishModelCount = Object.values(modelsInfo).reduce(
      (acc, modelInfo) => {
        if (modelInfo.shouldPublish) {
          return acc + 1;
        }

        return acc;
      },
      0
    );
    const { databaseId } = databaseInfo;

    if (!databasesById[databaseId] && publishModelCount < 2) {
      if (hasValidMappings) {
        return [
          ModelNamespacePanelNextButtonType.MAPPING,
          TEST_IDS.publishProjectDrawer.footer.nextToMappingButton,
        ];
      }

      return [
        ModelNamespacePanelNextButtonType.PUBLISH,
        TEST_IDS.publishProjectDrawer.footer.publishButton,
      ];
    }

    return [
      ModelNamespacePanelNextButtonType.COMBO_MODEL,
      TEST_IDS.publishProjectDrawer.footer.nextToAliasButton,
    ];
  }, [databaseInfo, databasesById, hasValidMappings, modelsInfo]);

  const handleNext = () => {
    if (nextButtonError) {
      return;
    }

    if (nextButtonType === ModelNamespacePanelNextButtonType.MAPPING) {
      const props: MappingConfigPanelProps = {
        databaseInfo,
        isConsolidatingModels,
        isPublishingRules,
        modelsInfo,
        prefixInfoArr,
        ruleInfo,
        spgInfo,
      };
      openPanel({ renderPanel: MappingConfigPanel, props });
    } else if (nextButtonType === ModelNamespacePanelNextButtonType.PUBLISH) {
      const props: OpenInExplorerPanelProps = {
        databaseInfo,
        isPublishingRules,
        modelsInfo,
        ruleInfo,
      };
      openPanel({ renderPanel: OpenInExplorerPanel, props });
      publish({
        ...props,
        isConsolidatingModels,
        prefixInfoArr,
        projectId,
        spgInfo,
      });
    } else {
      const props: ModelAliasConfigPanelProps = {
        databaseInfo,
        isConsolidatingModels,
        isPublishingRules,
        modelsInfo,
        prefixInfoArr,
        ruleInfo,
        spgInfo,
      };
      openPanel({ renderPanel: ModelAliasConfigPanel, props });
    }
  };

  const checked = prefixInfoArr.filter(
    ({ isConflicted, shouldPublish }) => isConflicted && shouldPublish
  );
  const handleToggleAllChecked = () => {
    const anyChecked = checked.length;
    const shouldPublish = !anyChecked;
    setPrefixInfoArr(
      prefixInfoArr.map((prefixInfo, idx) => {
        // only check prefixes that are conflicted
        if (!prefixInfo.isConflicted) {
          return prefixInfo;
        }
        // only check prefixes that are currently valid
        if (prefixErrors[idx].prefix) {
          return prefixInfo;
        }
        return {
          ...prefixInfo,
          shouldPublish,
        };
      })
    );
  };
  const conflictedLength = prefixInfoArr.filter(
    (prefixInfo) => prefixInfo.isConflicted
  ).length;
  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.publishProjectDrawer.namespace.container}
      >
        <PublishProjectDrawerStepper active="model" />
        <div>
          <div className={classNames('sd-prefix-header', 'sd-prefix-grid')}>
            <span>
              <strong>
                {
                  copy.components.publishProjectDrawer.mappingConfiguration
                    .shouldPublish
                }
              </strong>
              <Checkbox
                checked={checked.length === conflictedLength}
                indeterminate={Boolean(
                  checked.length && checked.length !== conflictedLength
                )}
                onChange={handleToggleAllChecked}
              />
            </span>
            <span>
              {
                copy.components.publishProjectDrawer.namespace.columnHeader
                  .prefix
              }
            </span>
            <span>
              {
                copy.components.publishProjectDrawer.namespace.columnHeader
                  .namespaceIri
              }
            </span>
          </div>
          {prefixInfoArr.map((prefixInfo, idx) => {
            if (!prefixInfo.isConflicted) {
              return null;
            }

            return (
              <ModelNamespaceRow
                // sadly, this can be in a state state where there is no unique usable key
                // (even though we will show an error in that state)
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                error={prefixErrors[idx]}
                index={idx}
                onUpdatePrefixInfo={handleUpdatePrefixInfo}
                prefixInfo={prefixInfo}
              />
            );
          })}
        </div>
      </div>
      <PublishProjectDrawerFooter
        handleBack={closePanel}
        handleNext={handleNext}
        nextError={nextButtonError}
        nextTestId={nextButtonTestId}
        nextText={
          nextButtonType === ModelNamespacePanelNextButtonType.COMBO_MODEL ||
          nextButtonType === ModelNamespacePanelNextButtonType.MAPPING
            ? copy.components.publishProjectDrawer.footer.next
            : copy.components.publishProjectDrawer.footer.publish
        }
      />
    </>
  );
};
